/**
 * @file TwoFactorSetup.jsx
 * @description Handles two-factor authentication setup and management
 *
 * @section Responsibilities
 * - Display QR code for 2FA setup
 * - Handle token verification
 * - Manage backup codes
 * - Enable/disable 2FA
 * - Show 2FA status
 *
 * @section Dependencies
 * - Request: API communication
 * - QRCode: QR code display
 * - Session utilities
 *
 * @section Integration Points
 * - 2FA API: Setup and verification
 * - Authentication system
 * - User settings
 *
 * @section State Management
 * - setupData: QR code and setup information
 * - verificationToken: User input token
 * - backupCodes: Generated backup codes
 * - status: 2FA enabled/disabled status
 * - loading: Process status
 *
 * @section Events
 * - onSetup: Initiate 2FA setup
 * - onVerify: Verify setup token
 * - onDisable: Disable 2FA
 * - onRegenerateBackup: Generate new backup codes
 *
 * @section Props
 * @prop {Function} onComplete - Completion callback
 * @prop {Boolean} showBackupCodes - Controls backup codes display
 */

import React, { useState, useEffect, useCallback } from "react";
import { Grid, Box, Typography, Paper, Alert } from "@mui/material";
import QRCode from "qrcode.react";
import OtpInput from "react-otp-input";
import DgButton from "../../components/DgButton";
import Request from "../../utils/fetchUtils";
import Swal from "sweetalert2";
import "./../../styles/two-factor.css";

const TwoFactorSetup = ({ onComplete, showBackupCodes = true }) => {
  const [step, setStep] = useState("status"); // status, setup, verify, complete
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationToken, setVerificationToken] = useState("");  const [backupCodes, setBackupCodes] = useState([]);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [canContinue, setCanContinue] = useState(false);
  const [countdown, setCountdown] = useState(3);  const checkTwoFactorStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Request("/user/2fa/status", {}, "GET");
      if (response.ok) {
        setStatus(response.data);
        // Only set step if we're currently on status step (initial load)
        // This prevents interference with other steps like setup, verify, complete
        if (step === "status") {
          setStep(response.data.enabled ? "status" : "status");
        }
      }
    } catch (error) {
      setError("Failed to check 2FA status");
    } finally {
      setLoading(false);
    }
  }, [step]);  
  
  useEffect(() => {
    if (step === "status") {
      checkTwoFactorStatus();
    }
  }, [checkTwoFactorStatus, step]);

  useEffect(() => {
    const loadInitialStatus = async () => {
      setLoading(true);
      try {
        const response = await Request("/user/2fa/status", {}, "GET");
        if (response.ok) {
          setStatus(response.data);
          setStep(response.data.enabled ? "status" : "status");
        }
      } catch (error) {
        setError("Failed to check 2FA status");
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialStatus();
  }, []); // Empty dependency array - only runs on mount
  // Enable continue button after 3 seconds on backup codes screen
  useEffect(() => {
    if (step === "complete" && backupCodes.length > 0) {
      setCanContinue(false);
      setCountdown(3);
      
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setCanContinue(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }  }, [step, backupCodes]);

  const initiateTwoFactorSetup = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await Request("/user/2fa", {}, "GET");
      if (response.ok) {
        setSetupData(response.data);
        setStep("setup");
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError("Failed to initiate 2FA setup");
    } finally {
      setLoading(false);
    }
  };
  const verifyTwoFactorSetup = async () => {
    if (verificationToken.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await Request(
        "/user/2fa/verify",
        { token: verificationToken },
        "POST"
      );
      if (response.ok) {
        setBackupCodes(response.data.backupCodes);
        setStep("complete");
        await checkTwoFactorStatus();
        // Don't call onComplete() here - let user see backup codes first
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError("Failed to verify 2FA token");
    } finally {
      setLoading(false);
    }
  };

  const handleSetupComplete = () => {
    // In setup flow (showBackupCodes = false), don't change step automatically
    // Let user stay on backup codes screen until they manually close
    if (!showBackupCodes) {
      // Stay on complete step, don't change to status
      return;
    }
    // Only change step for profile context
    setStep("status");
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join("\n");
    navigator.clipboard.writeText(codesText).then(() => {
      Swal.fire("Copied", "Backup codes copied to clipboard", "success");
    });
  };

  const downloadBackupCodes = () => {
    const codesText = backupCodes.join("\n");
    const blob = new Blob([codesText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ad-deum-backup-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading && step === "status") {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <Typography>Checking 2FA status...</Typography>
      </Box>
    );
  }  return (    <Paper elevation={3} sx={{ 
      p: 4, 
      maxWidth: 600, 
      mx: "auto",
      backgroundColor: "#021C2D",
      borderRadius: "12px",
      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
      border: "1px solid #274A61"
    }} className="two-factor-setup">
      <Typography variant="h5" gutterBottom className="two-factor-title" sx={{ 
        color: "#eeeeee",
        fontFamily: "HomepageBaukasten",
        textAlign: "center",
        marginBottom: "24px"
      }}>
        Two-Factor Authentication
      </Typography>      {error && (
        <Alert severity="error" sx={{ 
          mb: 2,
          backgroundColor: "rgba(255, 94, 91, 0.1)",
          border: "1px solid #FF5E5B",
          color: "#FF5E5B",
          borderRadius: "8px"
        }}>
          {error}
        </Alert>
      )}        {step === "status" && (
        <Box>
          {status?.enabled ? (
            <Box>
              <Alert severity="success" sx={{ 
                mb: 3,
                backgroundColor: "rgba(161, 188, 168, 0.1)",
                border: "1px solid #A1BCA8",
                color: "#A1BCA8",
                borderRadius: "12px",
                fontSize: "16px",
                fontWeight: "600"
              }}>
                🛡️ Two-factor authentication is active and protecting your account!
              </Alert>
              
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "space-between",
                backgroundColor: "#012B45",
                borderRadius: "12px",
                padding: "20px",
                border: "1px solid #274A61",
                mb: 3
              }}>
                <Box>
                  <Typography variant="h6" sx={{ 
                    color: "#eeeeee", 
                    fontSize: "18px", 
                    fontWeight: "600",
                    mb: 1 
                  }}>
                    Security Status
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#95B2C5", mb: 1 }}>
                    Your account is protected with two-factor authentication
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: "#A1BCA8", 
                    fontSize: "14px",
                    fontWeight: "500"
                  }}>
                    ✓ Backup codes available: {status.backupCodesCount}
                  </Typography>
                </Box>
                <Box sx={{ 
                  width: "60px", 
                  height: "60px", 
                  borderRadius: "50%", 
                  backgroundColor: "#A1BCA8", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  fontSize: "24px"
                }}>
                  🔒
                </Box>
              </Box>              
              {/* Show minimal interface in profile context */}
              {showBackupCodes && (
                // Profile context - show clean status only, no action buttons
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="body2" sx={{ 
                    color: "#95B2C5", 
                    fontSize: "16px",
                    fontStyle: "italic"
                  }}>
                    Your account is secure and protected with two-factor authentication.
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              <Alert severity="warning" sx={{ 
                mb: 2,
                backgroundColor: "rgba(255, 189, 48, 0.1)",
                border: "1px solid #FFBD30",
                color: "#FFBD30"
              }}>
                Two-factor authentication is not enabled
              </Alert>
              <Typography variant="body1" sx={{ mb: 3, color: "#95B2C5" }}>
                Add an extra layer of security to your account by enabling two-factor authentication.
              </Typography>
              <DgButton
                category="primary"
                text="Setup Two-Factor Authentication"
                action={initiateTwoFactorSetup}
                loading={loading}
                style={{ padding: "16px 32px", fontSize: "16px" }}
              />
            </Box>
          )}
        </Box>
      )}      {step === "setup" && setupData && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ 
            color: "#eeeeee",
            fontSize: "20px",
            fontWeight: "600",
            marginBottom: "16px"
          }}>
            Step 1: Scan QR Code
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: "#95B2C5", fontSize: "16px" }}>
            Use your authenticator app (Google Authenticator, Authy, etc.) to scan this QR code:
          </Typography><Box display="flex" justifyContent="center" sx={{ mb: 3 }} className="qr-code-container">
            <Box sx={{ 
              backgroundColor: "#f8f9fa", 
              padding: "20px", 
              borderRadius: "12px",
              border: "1px solid #274A61"
            }}>
              <QRCode value={setupData.qrCode} size={200} />
            </Box>
          </Box>          <Typography variant="body2" sx={{ mb: 2, color: "#95B2C5", fontSize: "16px" }}>
            Can't scan? Enter this code manually:
          </Typography><Paper variant="outlined" sx={{ 
            p: 2, 
            mb: 3, 
            fontFamily: "monospace",
            backgroundColor: "#012B45",
            borderColor: "#274A61",
            color: "#95B2C5"
          }} className="manual-entry-key">
            {setupData.manualEntryKey}
          </Paper>          <Typography variant="h6" gutterBottom sx={{ 
            color: "#eeeeee",
            fontSize: "20px",
            fontWeight: "600",
            marginBottom: "16px",
            marginTop: "24px"
          }}>
            Step 2: Enter Verification Code
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: "#95B2C5", fontSize: "16px" }}>
            Enter the 6-digit code from your authenticator app:
          </Typography><Box display="flex" justifyContent="center" sx={{ mb: 3 }} className="token-input-container">
            <OtpInput
              value={verificationToken}
              onChange={setVerificationToken}
              numInputs={6}
              renderSeparator={<span style={{ width: "8px" }}></span>}
              renderInput={(props) => (
                <input
                  {...props}
                  className="token-input"
                  style={{                    width: "45px",
                    height: "45px",
                    margin: "0 6px",
                    fontSize: "20px",
                    borderRadius: "8px",
                    border: "2px solid #274A61",
                    textAlign: "center",
                    outline: "none",
                    backgroundColor: "#012B45",
                    color: "#eeeeee",
                    fontWeight: "600"
                  }}                  onFocus={(e) => {
                    e.target.style.border = "2px solid #A1BCA8";
                    e.target.style.boxShadow = "0 0 0 2px rgba(161, 188, 168, 0.2)";
                  }}
                  onBlur={(e) => {
                    e.target.style.border = "2px solid #274A61";
                    e.target.style.boxShadow = "none";
                  }}
                />
              )}
            />
          </Box>          <Grid container spacing={2} justifyContent="center" sx={{ mt: 2 }}>
            <Grid item>              <DgButton
                category="secondary"
                text="Cancel"
                action={() => setStep("status")}
                style={{ padding: "16px 32px", fontSize: "16px" }}
              />
            </Grid>
            <Grid item>
              <DgButton
                category="primary"
                text="Verify & Enable"
                action={verifyTwoFactorSetup}
                loading={loading}
                disabled={verificationToken.length !== 6}
                style={{ padding: "16px 32px", fontSize: "16px" }}
              />
            </Grid>
          </Grid>
        </Box>
      )}      {step === "complete" && backupCodes.length > 0 && (
        <Box>
          <Alert severity="success" sx={{ 
            mb: 3,
            backgroundColor: "rgba(161, 188, 168, 0.1)",
            border: "1px solid #A1BCA8",
            color: "#A1BCA8",
            borderRadius: "8px",
            fontSize: "16px",
            fontWeight: "600"
          }}>
            Two-factor authentication has been enabled successfully!
          </Alert>
            <Typography variant="h6" gutterBottom sx={{ 
            color: "#eeeeee",
            fontSize: "20px",
            fontWeight: "600",
            marginBottom: "16px"
          }}>
            Important: Save Your Backup Codes
          </Typography>
          <Typography variant="body2" sx={{ 
            mb: 3, 
            color: "#95B2C5",
            fontSize: "16px",
            lineHeight: "1.6"
          }}>
            These backup codes can be used to access your account if you lose your authenticator device.
            <strong style={{ color: "#eeeeee" }}> Please save them in a secure location before continuing.</strong>
          </Typography>          <Paper variant="outlined" sx={{ 
            p: 3, 
            mb: 3,
            backgroundColor: "#012B45",
            borderColor: "#274A61",
            borderRadius: "12px",
            border: "2px solid #274A61"
          }} className="backup-codes-container">
            <Grid container spacing={2} className="backup-codes-grid">
              {backupCodes.map((code, index) => (
                <Grid item xs={6} key={index}>
                  <Typography variant="body2" sx={{ 
                    fontFamily: "monospace",
                    color: "#eeeeee",
                    fontSize: "16px",
                    fontWeight: "600",
                    padding: "8px 12px",
                    backgroundColor: "#021C2D",
                    borderRadius: "6px",
                    textAlign: "center",
                    border: "1px solid #274A61"
                  }} className="backup-code">
                    {code}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <DgButton
                category="secondary"
                text="Copy Codes"
                action={copyBackupCodes}
                style={{ padding: "16px 32px", fontSize: "16px" }}
              />
            </Grid>
            <Grid item>
              <DgButton
                category="secondary"
                text="Download Codes"
                action={downloadBackupCodes}
                style={{ padding: "16px 32px", fontSize: "16px" }}
              />
            </Grid>
            {!showBackupCodes ? (
              <>
                <Grid item>
                  <DgButton
                    category="success"
                    text="Close"
                    action={() => onComplete()}
                    style={{ 
                      padding: "16px 32px", 
                      fontSize: "16px",
                      fontWeight: "600"
                    }}
                  />
                </Grid>
              </>
            ) : (
              // Profile context - continue button
              <Grid item>
                <DgButton
                  category="primary"
                  text={canContinue ? "Continue" : `Continue (${countdown}s)`}
                  action={handleSetupComplete}
                  disabled={!canContinue}
                  style={{ padding: "16px 32px", fontSize: "16px" }}
                />
              </Grid>
            )}
          </Grid>
            {/* Additional message for setup flow */}
          {!showBackupCodes && (
            <Box sx={{ mt: 3, p: 2, backgroundColor: "#012B45", borderRadius: "8px", border: "1px solid #274A61" }}>
              <Typography variant="body2" sx={{ 
                color: "#A1BCA8", 
                fontSize: "16px",
                fontWeight: "600",
                textAlign: "center",
                mb: 1
              }}>
                🔒 Your backup codes are safely displayed above
              </Typography>
              <Typography variant="body2" sx={{ 
                color: "#95B2C5", 
                fontSize: "14px",
                textAlign: "center",
                fontStyle: "italic"
              }}>
                They will remain visible until you close this setup. Take your time to save them securely.
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default TwoFactorSetup;
